import { TIER_STAT_MULT } from "@zend/content";
import { mulberry32 } from "../rng.js";
import { ROLE_BASE, levelMultiplier, starMultiplier } from "../units/stats.js";
import { tickCorrode, useAbility } from "./abilities.js";
import { hashEvents } from "./hash.js";
import { combatantMeta, resolveAbilities, slotKind, turretCombatant } from "./snapshot.js";
import { auraPct, effectiveInterval, energyGain, expireStatuses, hpShare, livingUnits, protocolFromRule, status, type BattleState, type SideState, type Unit } from "./state.js";
import { bySlot, deadFighter, targetPool } from "./targeting.js";
import { battleMorale } from "./tactics.js";
import { ENERGY_MAX, MAX_TICKS, ULTIMATE_COOLDOWN_TICKS, type BattleContext, type BattleInput, type BattleResult, type CombatantInput, type Side, type SideInput, type UnitSummary, type Winner } from "./types.js";

function makeUnit(input: CombatantInput, side: Side, sideState: SideState, startEnergy = 0): Unit {
  const meta = combatantMeta(input);
  const abilities = resolveAbilities(input);
  const kind = slotKind(input.slot);
  const hpMult = (1 + (sideState.modifiers.hpPct ?? 0) / 100) * (1 + auraPct(sideState, "hp") / 100);
  const maxHp = Math.max(1, Math.round(input.stats.hp * hpMult));
  const pulse = abilities.find((a) => a.archetype === "abl.command_pulse");
  const protocolAb = abilities.find((a) => a.archetype === "abl.protocol");
  return {
    input,
    meta,
    side,
    slot: input.slot,
    kind,
    stats: input.stats,
    maxHp,
    hp: Math.max(1, Math.round(maxHp * (Math.max(1, Math.min(100, input.hpPct)) / 100))),
    energy: Math.max(0, Math.min(ENERGY_MAX, Math.round(startEnergy))),
    shield: 0,
    alive: true,
    statuses: [],
    nextAttackTick: input.stats.intervalTicks,
    abilities,
    protocol: input.protocol ?? (pulse ? protocolFromRule(pulse.params["rule"]) : protocolAb ? protocolFromRule(protocolAb.params["rule"]) : null),
    protocolBonusPct: protocolAb ? (protocolAb.params["bonusPct"] ?? 0) * protocolAb.magnitude : 0,
    damageDealt: 0,
    damageTaken: 0,
    healing: 0,
    kills: 0,
    abilitiesUsed: 0,
  };
}

/** Share of the front's combined health the commander's own frame carries (R38; 100 % when unset). */
function commandFrame(commander: Unit): number {
  return Math.max(0.5, (commander.input.commandFramePct ?? 100) / 100);
}

function buildSide(side: Side, input: SideInput, startEnergy: number, rulesVersion: 1 | 2 | 3 = 1): SideState {
  const state: SideState = {
    side,
    units: [],
    modifiers: { atkPct: input.modifiers?.atkPct ?? 0, defPct: input.modifiers?.defPct ?? 0, hpPct: input.modifiers?.hpPct ?? 0, auraPct: input.modifiers?.auraPct ?? 0, revengePct: input.modifiers?.revengePct ?? 0 },
    summoned: 0,
    wallHp: 0,
    wallAbsorbed: 0,
  };
  // Supports first so HP auras are visible when the fighters' max HP is computed.
  const ordered = [...input.members].sort((a, b) => (slotKind(a.slot) === "support" ? -1 : 0) - (slotKind(b.slot) === "support" ? -1 : 0));
  for (const m of ordered) state.units.push(makeUnit(m, side, state, startEnergy));
  state.units.sort(bySlot);
  // Commander HP: the combined health of the fielded front times its command frame (R38, D76); before
  // rules version 3 it was the average of the fielded fighters (R32, D17).
  const commander = state.units.find((u) => u.kind === "commander");
  if (commander && commander.input.kind === "unit") {
    const cosmeticHp = 1 + (commander.input.commanderHpPct ?? 0) / 100;
    const frame = rulesVersion >= 3 ? commandFrame(commander) : 1;
    const fighters = state.units.filter((u) => u.kind === "fighter");
    if (fighters.length) {
      const total = fighters.reduce((s, f) => s + f.maxHp, 0);
      const avg = rulesVersion >= 3 ? total * frame : Math.round(total / fighters.length);
      commander.maxHp = Math.max(1, Math.round(avg * cosmeticHp));
      commander.hp = Math.max(1, Math.round(commander.maxHp * (Math.max(1, Math.min(100, commander.input.hpPct)) / 100)));
    } else {
      // No fighters fielded: the commander keeps its own base HP through the normal multipliers.
      const own = ROLE_BASE.commander.hp * TIER_STAT_MULT[commander.meta.tier] * levelMultiplier(commander.input.level) * starMultiplier(commander.input.stars);
      commander.maxHp = Math.max(1, Math.round(own * frame * cosmeticHp));
      commander.hp = Math.max(1, Math.round(commander.maxHp * (Math.max(1, Math.min(100, commander.input.hpPct)) / 100)));
    }
  }
  return state;
}

/** Replay HP overlays use the same commander and aura initialization as the simulation. */
export function initialBattleHealth(input: BattleInput): Record<string, { hp: number; max: number; alive: boolean }> {
  const health: Record<string, { hp: number; max: number; alive: boolean }> = {};
  for (const side of ["attacker", "defender"] as const) {
    const state = buildSide(side, input[side], input.startEnergy ?? 0, input.rulesVersion ?? 1);
    if (side === "defender") input.structures?.turrets.forEach((turret, index) => state.units.push(makeUnit(turretCombatant(`turret_${index + 1}`, turret.hp, turret.atk), side, state)));
    for (const unit of state.units) health[unit.input.id] = { hp: unit.hp, max: unit.maxHp, alive: true };
  }
  return health;
}

/**
 * Defeat (R38, D76): a squad with a commander fights on until that commander falls — an empty front no longer
 * ends the battle, it only opens the way to the commander. A side without a commander (a pack, a lone garrison)
 * is beaten when nothing but turrets is left. Rules versions 1 and 2 keep the old "front is gone" surrender.
 */
function sideDefeated(side: SideState, rulesVersion: 1 | 2 | 3): boolean {
  const commander = side.units.find((u) => u.kind === "commander");
  const fighters = side.units.filter((u) => u.kind === "fighter");
  if (commander) return !commander.alive || (rulesVersion < 3 && fighters.length > 0 && fighters.every((f) => !f.alive));
  if (rulesVersion < 3 && fighters.length > 0 && fighters.every((f) => !f.alive)) return true;
  return livingUnits(side).every((u) => u.kind === "turret");
}

function timeoutWinner(context: BattleContext, state: BattleState): Side {
  if (context === "arena") return hpShare(state.sides.attacker) >= hpShare(state.sides.defender) ? "attacker" : "defender";
  return "defender";
}

/** Reboots need a wrecked fighter; every other ability is always usable once charged. */
function usable(state: BattleState, u: Unit, ab: { archetype: string; readyAtTick: number; usedOnce: boolean }): boolean {
  if (ab.usedOnce || state.tick < ab.readyAtTick) return false;
  if (ab.archetype === "abl.reboot" || ab.archetype === "abl.ultimate_reboot") return deadFighter(state, u) !== null;
  return true;
}

function act(state: BattleState, u: Unit): void {
  if (status(u, "stun")) return;
  // Ultimate when charged and recharged, else the next active in turn, else the standard attack (R30).
  const ultimate = u.abilities.find((a) => a.kind === "ultimate");
  const actives = u.abilities.filter((a) => a.kind === "active");
  let chosen = u.abilities.find((a) => a.kind === "standard") ?? u.abilities[0]!;
  let cost = 0;
  if (ultimate && u.energy >= ENERGY_MAX && usable(state, u, ultimate)) {
    chosen = ultimate;
    cost = ENERGY_MAX;
    ultimate.readyAtTick = state.tick + ULTIMATE_COOLDOWN_TICKS;
  } else if (actives.length && u.energy >= ENERGY_MAX) {
    const start = u.abilitiesUsed % actives.length;
    for (let i = 0; i < actives.length; i++) {
      const candidate = actives[(start + i) % actives.length]!;
      if (usable(state, u, candidate)) {
        chosen = candidate;
        cost = ENERGY_MAX;
        break;
      }
    }
  }
  if (cost > 0) u.energy -= cost;
  else u.energy = Math.min(ENERGY_MAX, u.energy + energyGain(state, u, u.stats.energyPerAttack));
  useAbility(state, u, chosen);
  if (chosen.kind === "standard") {
    // Pulses ride on the standard attack of commanders and supports (R32).
    for (const pulse of u.abilities.filter((a) => a.kind === "pulse")) if (u.alive) useAbility(state, u, pulse);
  }
}

/** Runs one deterministic battle (40-combat.md R30–R32). */
export function simulate(input: BattleInput): BattleResult {
  const rng = mulberry32(input.seed >>> 0);
  const state: BattleState = {
    rulesVersion: input.rulesVersion ?? 1,
    tick: 0,
    rng,
    context: input.context,
    sides: { attacker: buildSide("attacker", input.attacker, input.startEnergy ?? 0, input.rulesVersion ?? 1), defender: buildSide("defender", input.defender, input.startEnergy ?? 0, input.rulesVersion ?? 1) },
    events: [],
    trapDamage: 0,
    turretDamage: 0,
    nextExtraId: 0,
  };
  const structures = input.structures;
  if (structures) {
    const def = state.sides.defender;
    def.wallHp = Math.max(0, Math.round(structures.wallHp));
    structures.turrets.forEach((t, i) => def.units.push(makeUnit(turretCombatant(`turret_${i + 1}`, t.hp, t.atk), "defender", def)));
  }
  const maxTicks = input.maxTicks ?? MAX_TICKS;
  const refreshMorale = () => {
    if (state.rulesVersion !== 2) return;
    for (const side of [state.sides.attacker, state.sides.defender]) {
      const morale = battleMorale(side.units.map(u => ({ kind: u.input.kind, slot: u.slot, role: u.meta.role, alive: u.alive })));
      if (morale && morale.score !== side.morale?.score) state.events.push({ t: state.tick, k: "morale", a: side.side, v: morale.score, x: `${morale.atkPct}` });
      side.morale = morale;
    }
  };
  refreshMorale();
  let outcome: Winner | null = null;
  for (state.tick = 1; state.tick <= maxTicks; state.tick++) {
    if (state.tick === 1 && structures && structures.traps.count > 0) {
      // Traps hit the attacking front round-robin before the first exchange (R17).
      const targets = targetPool(state, "defender");
      const trapper = livingUnits(state.sides.defender)[0];
      for (let i = 0; i < structures.traps.count && targets.length; i++) {
        const t = targets[i % targets.length]!;
        if (!t.alive) continue;
        const dmg = Math.max(1, Math.round(structures.traps.damage));
        t.hp -= dmg;
        t.damageTaken += dmg;
        state.trapDamage += dmg;
        state.events.push({ t: 1, k: "trap", a: trapper?.input.id ?? "", b: t.input.id, v: dmg });
        if (t.hp <= 0) {
          t.alive = false;
          t.hp = 0;
          state.events.push({ t: 1, k: "death", a: "", b: t.input.id });
        }
      }
    }
    for (const side of [state.sides.attacker, state.sides.defender]) {
      for (const u of side.units) {
        if (!u.alive) continue;
        expireStatuses(state, u);
        tickCorrode(state, u);
      }
    }
    const sideOrder = [state.sides.attacker, state.sides.defender];
    // Equal timers must not always give the attacking player first strike. Legacy order is frozen.
    if (state.rulesVersion === 2 && sideOrder.every(side => side.units.some(u => u.alive && state.tick >= u.nextAttackTick && !status(u, "stun"))) && state.rng.chance(0.5)) sideOrder.reverse();
    for (const side of sideOrder) {
      // Copy: summons may append during iteration.
      refreshMorale();
      for (const u of [...side.units]) {
        if (!u.alive || state.tick < u.nextAttackTick) continue;
        if (status(u, "stun")) {
          u.nextAttackTick = state.tick + 1;
          continue;
        }
        act(state, u);
        refreshMorale();
        u.nextAttackTick = state.tick + effectiveInterval(u);
        if (sideDefeated(state.sides.attacker, state.rulesVersion ?? 1)) {
          outcome = "defender";
          break;
        }
        if (sideDefeated(state.sides.defender, state.rulesVersion ?? 1)) {
          outcome = "attacker";
          break;
        }
      }
      if (outcome) break;
    }
    if (outcome) break;
  }
  let winner: Side;
  if (outcome) winner = outcome;
  else {
    outcome = "timeout";
    winner = timeoutWinner(input.context, state);
    state.tick = maxTicks;
  }
  state.events.push({ t: state.tick, k: "end", x: outcome === "timeout" ? `timeout:${winner}` : winner });
  const units: UnitSummary[] = [];
  for (const side of [state.sides.attacker, state.sides.defender]) {
    for (const u of side.units) {
      units.push({
        id: u.input.id,
        defId: u.input.defId,
        side: u.side,
        slot: u.slot,
        damageDealt: u.damageDealt,
        damageTaken: u.damageTaken,
        healing: u.healing,
        kills: u.kills,
        abilitiesUsed: u.abilitiesUsed,
        alive: u.alive,
        hpPct: u.alive ? Math.max(1, Math.round((u.hp / u.maxHp) * 100)) : 0,
      });
    }
  }
  const winners = units.filter((u) => u.side === winner && u.slot !== "turret");
  const mvp = winners.length ? winners.reduce((m, u) => (u.damageDealt + u.healing > m.damageDealt + m.healing ? u : m), winners[0]!).id : null;
  return {
    winner,
    outcome,
    ticks: state.tick,
    units,
    mvp,
    hpShare: { attacker: hpShare(state.sides.attacker), defender: hpShare(state.sides.defender) },
    structures: { wallAbsorbed: state.sides.defender.wallAbsorbed, turretDamage: state.turretDamage, trapDamage: state.trapDamage },
    hash: hashEvents(state.events),
    events: state.events,
  };
}
